import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  getCustomersOptions,
  Customers,
} from '../../customer/customers/queryOptions';
import {
  getInsurancesOptions,
  Insurances,
} from '../../insurance/insurances/queryOptions';
import { getClaimsOptions, Claims } from '../../claim/claims/queryOptions';
import { match } from 'ts-pattern';
import { useState } from 'react';
import {
  getQuotationsOptions,
  Quotations,
} from '../../quotation/quotations/queryOptions';

const CATEGORIES = [
  { value: 'insurance', label: 'Verzekering', meta: {} },
  { value: 'customer', label: 'Klant', meta: {} },
  { value: 'quotation', label: 'Offerte', meta: {} },
  { value: 'claim', label: 'Claim', meta: {} },
] as const;

export type Category = (typeof CATEGORIES)[number]['value'];

export function useGetDataPerCategory() {
  const state = useState<{
    query: string | undefined;
    category: Category | undefined;
  }>({ query: undefined, category: undefined });

  const customers = useQuery({
    ...getCustomersOptions({
      search: state[0].query,
    }),
    enabled: filterIsCustomer(state[0].category),
  });

  const insurances = useQuery({
    ...getInsurancesOptions({
      search: state[0].query,
    }),
    enabled: filterIsIsurance(state[0].category),
  });

  const claims = useQuery({
    ...getClaimsOptions({
      search: state[0].query,
    }),
    enabled: filterIsClaim(state[0].category),
  });

  const Quotations = useQuery({
    ...getQuotationsOptions({
      search: state[0].query,
    }),
    enabled: filterIsQuotation(state[0].category),
  });

  return {
    result: match(state[0].category)
      .returnType<
        | ({ type: 'customer' } & UseQueryResult<Customers>)
        | ({ type: 'insurance' } & UseQueryResult<Insurances>)
        | ({ type: 'claim' } & UseQueryResult<Claims>)
        | ({ type: 'quotation' } & UseQueryResult<Quotations>)
        | ({ type: 'none' } & UseQueryResult<[]>)
      >()
      .when(filterIsCustomer, () => ({ type: 'customer', ...customers }))
      .when(filterIsIsurance, () => ({ type: 'insurance', ...insurances }))
      .when(filterIsClaim, () => ({ type: 'claim', ...claims }))
      .when(filterIsQuotation, () => ({ type: 'quotation', ...Quotations }))
      .otherwise(() => ({ type: 'none', ...[] })),
    CATEGORIES,
    state,
  } as const;
}

const filterIsIsurance = (c: Category | undefined) => c === 'insurance';
const filterIsCustomer = (c: Category | undefined) => c === 'customer';
const filterIsClaim = (c: Category | undefined) => c === 'claim';
const filterIsQuotation = (c: Category | undefined) => c === 'quotation';
